import React, { useEffect, useState } from "react";

import { notificationsService } from "api/services/notifications.service";
import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input } from "components/UI/Input";
import Modal from "components/UI/Modal";
import Table, { Column } from "components/UI/Table";

import type { Notification } from "utils/type/notification";
import "pages/AdminPage.scss";
import "./NotificationManagement.scss";

const TYPE_LABEL: Record<string, string> = {
  RESERVATION_CONFIRMED: "예약 확정",
  RESERVATION_CANCELLED: "예약 취소",
  LESSON_REMINDER: "강습 리마인더",
  PRACTICE_REMINDER: "연습 리마인더",
  PAYMENT_COMPLETED: "결제 완료",
  SCHEDULE_APPROVED: "스케줄 승인",
  SCHEDULE_REJECTED: "스케줄 거절",
  TOUR_REMINDER: "투어 리마인더",
};

const NotificationManagement: React.FC = () => {
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  // 삭제 모달
  const [deleteModal, setDeleteModal] = useState<{
    open: boolean;
    notification: Notification | null;
  }>({ open: false, notification: null });
  const [actionLoading, setActionLoading] = useState(false);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await notificationsService.list();
      setItems(Array.isArray(res.data) ? res.data : (res.data.results ?? []));
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await notificationsService.markAllRead();
      fetchList();
    } catch {
      // ignore
    }
  };

  const handleMarkRead = async (id: number) => {
    try {
      await notificationsService.markRead(id);
      fetchList();
    } catch {
      // ignore
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.notification) return;
    setActionLoading(true);
    try {
      await notificationsService.remove(deleteModal.notification.id);
      setDeleteModal({ open: false, notification: null });
      fetchList();
    } catch {
      // ignore
    } finally {
      setActionLoading(false);
    }
  };

  const filteredItems = items.filter((item) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return item.title.toLowerCase().includes(q) || item.message.toLowerCase().includes(q);
  });

  const columns: Column<Notification>[] = [
    {
      key: "notification_type",
      title: "유형",
      width: "120px",
      render: (v) => <Badge variant="info">{TYPE_LABEL[String(v)] ?? String(v)}</Badge>,
    },
    { key: "title", title: "제목", width: "200px" },
    {
      key: "message",
      title: "내용",
      render: (v) => (
        <span className="notification-message">
          {String(v).slice(0, 80)}
          {String(v).length > 80 ? "..." : ""}
        </span>
      ),
    },
    {
      key: "is_read",
      title: "읽음",
      width: "60px",
      render: (v) => (v ? <Badge variant="draft">읽음</Badge> : <Badge variant="pending">미읽음</Badge>),
    },
    {
      key: "is_pushed",
      title: "Push",
      width: "60px",
      render: (v) => (v ? <Badge variant="active">발송</Badge> : <Badge variant="draft">미발송</Badge>),
    },
    {
      key: "created_at",
      title: "생성일",
      width: "140px",
      render: (v) => String(v).slice(0, 16).replace("T", " "),
    },
    {
      key: "actions",
      title: "관리",
      width: "140px",
      render: (_, row) => (
        <div className="notification-actions">
          {!row.is_read && (
            <Button size="sm" variant="ghost" onClick={() => handleMarkRead(row.id)}>
              읽음
            </Button>
          )}
          <Button size="sm" variant="danger" onClick={() => setDeleteModal({ open: true, notification: row })}>
            삭제
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader
        title="푸시 발송"
        description="푸시 발송을 관리합니다."
        actions={[
          <Button key="markall" variant="outline" onClick={handleMarkAllRead}>
            전체 읽음 처리
          </Button>,
        ]}
      />

      <div className="admin-page__card">
        <div className="admin-page__filter">
          <Input placeholder="제목, 내용 검색" value={search} onChange={(e) => setSearch(e.target.value)} prefix="🔍" />
        </div>

        {loading ? <div className="admin-page__loading">불러오는 중...</div> : <Table columns={columns} data={filteredItems} rowKey="id" />}
      </div>

      {/* 삭제 확인 모달 */}
      <Modal
        open={deleteModal.open}
        title="알림 삭제"
        width="440px"
        onClose={() => setDeleteModal({ open: false, notification: null })}
        footer={
          <div className="modal-footer-actions">
            <Button variant="secondary" onClick={() => setDeleteModal({ open: false, notification: null })}>
              취소
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={actionLoading}>
              {actionLoading ? "삭제 중..." : "삭제"}
            </Button>
          </div>
        }
      >
        <p className="confirm-text">
          이 알림을 삭제하시겠습니까?
          <br />
          <strong>{deleteModal.notification?.title}</strong>
        </p>
      </Modal>
    </div>
  );
};

export default NotificationManagement;
